import { z } from 'zod';
import { isTruthy, isTuple } from './typing.js';
// ========== schema generation helper methods ==========
/**
 * Given a string enum, `enumReference`, creates a `union` of `literal`s with the values of the enum.
 * Works with enums with two or more values.
 * Additionally works with enum-like objects.
 */
export function enumToZodLiteralUnion(enumReference) {
    const values = Object.values(enumReference);
    const minLength = 2;
    if (!isTuple(values, minLength)) {
        throw new Error('A Union Requires at Least Two Enum Values');
    }
    // * explicit as conversion to satisfy when `map` is called on `values` is still a tuple of the same size as validated above
    const literals = values.map((value) => z.literal(value.toString()));
    return z.union(literals);
}
/**
 * Creates a preprocessor schema converting ALL [falsy values](https://developer.mozilla.org/en-US/docs/Glossary/Falsy) into `val` when parsed.
 * Similar to [`prefault`](https://zod.dev/api?id=prefaults), but works for all falsy values instead of just `undefined`.
 */
function falsyTo(val) {
    return z.preprocess((v) => v || val, z.unknown());
}
/**
 * Converts ALL [falsy values](https://developer.mozilla.org/en-US/docs/Glossary/Falsy) into `null` instead, and allows `null` as a result, making them nullable.
 * The input type will be `unknown`, and the output type then becomes `T | null`.
 */
export function nullify(schema) {
    return falsyTo(null).pipe(schema.nullable());
}
/**
 * Converts ALL [falsy values](https://developer.mozilla.org/en-US/docs/Glossary/Falsy) into `undefined` instead, and allows `undefined` as a result, making them optional.
 * The input type will be `unknown`, and the output type then becomes `T | undefined`, and if used as part of an object schema, the property will be key optional instead of value optional.
 */
export function optionalize(schema) {
    return falsyTo(undefined).pipe(schema.optional());
}
/**
 * Trims input strings converting empty or whitespace-only strings into `null`, and allows `null` as a result.
 * Effectively, converting a nullish and [falsy](https://developer.mozilla.org/en-US/docs/Glossary/Falsy)-like strings into `null`.
 * Similar to `nullify`, but specifically for strings to retain the input type.
 */
export function nullifyString(schema) {
    return z
        .string()
        .trim()
        .nullish()
        .transform((v) => v || null)
        .pipe(schema.nullable());
}
/**
 * Trims input strings converting empty or whitespace-only strings into `undefined`, and allows `undefined` as a result.
 * Effectively, converting nullish and [falsy](https://developer.mozilla.org/en-US/docs/Glossary/Falsy)-like strings into `undefined`.
 * Similar to `optionalize`, but specifically for strings to retain the input type.
 */
export function optionalizeString(schema) {
    return z
        .string()
        .trim()
        .nullish()
        .transform((v) => v || undefined)
        .pipe(schema.optional());
}
// ========== predefined schemas ==========
const trimmedString = z.string().trim();
const nonEmptyString = trimmedString.min(1, { message: 'Must not be empty.' });
const csvList = trimmedString
    .transform((data) => 
// could be a single value or many, separated by a comma and optional whitespace
data
    .split(',')
    .map((value) => value.trim())
    .filter(isTruthy))
    .transform((data) => data.join(', ')); // rejoin to a single string with comma and space
const emailList = trimmedString
    .transform((data) => data
    .split(',')
    .map((value) => value.trim())
    .filter(isTruthy))
    .pipe(z.array(trimmedString.pipe(z.email()))) // could be a single email or a list of emails, separated by a comma and optional whitespace
    .transform((data) => data.join(', ')); // rejoin to a single string with comma and space
/** Results in a schema that will parse a stringified JSON value into the corresponding object if valid. */
const stringToJSONSchema = z.string().transform((str, ctx) => {
    try {
        const parsed = JSON.parse(str);
        return parsed;
    }
    catch {
        ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });
        return z.NEVER;
    }
});
/**
 * Lifted from [Zod Utils](https://github.com/JacobWeisenburger/zod_utilz/blob/main/src/stringToJSON.ts).
 *
 * `stringToJSON` is a schema that validates JSON encoded as a string, then returns the parsed value.
 *
 * @example
 * const schema = stringToJSON()
 * schema.parse('true') // true
 * schema.parse('null') // null
 * schema.parse('["one", "two", "three"]') // ['one', 'two', 'three']
 * schema.parse('<html>not a JSON string</html>') // throws
 * schema.pipe(z.object({foo: z.literal('bar')})).parse('{"foo":"bar"}') // {foo: 'bar'}
 */
export const stringToJSON = () => stringToJSONSchema;
/**
 * Creates a schema for numeric values to ensure they can be stored in a decimal with specific `precision` and `scale`.
 *
 * @param precision The total number of digits in the number.
 * @param scale The number of digits to the right of the decimal point.
 * @param message A custom error message if the validation fails.
 *
 * @see https://www.prisma.io/docs/orm/reference/prisma-schema-reference#decimal
 * @see https://www.postgresql.org/docs/current/datatype-numeric.html#DATATYPE-NUMERIC-DECIMAL
 */
const decimal = (precision, scale, message) => z.number().refine((value) => {
    const valueString = value.toString();
    const [integerPart, decimalPart] = valueString.split('.');
    if (!integerPart)
        return false;
    if (!decimalPart)
        return precision >= integerPart.length;
    return (precision >= integerPart.length + decimalPart.length &&
        scale >= decimalPart.length);
}, {
    message: message ??
        `Decimal must have at most ${precision} digits and at most ${scale} decimal places, otherwise it may overflow or be rounded`,
});
const insuranceProhibitedRefine = (value) => !/(insure|insurance|insured|insuring)/i.test(value);
const insuranceString = (max = Infinity) => nonEmptyString.max(max).refine(insuranceProhibitedRefine, {
    message: 'Please avoid using any variation of words like "insure" or "insurance"',
});
export const maxPolicyEligibilityDays = 999_999;
/** Predefined, reusable schemas with user-friendly error messages. */
// ? possibly rename, or restructure to be more similar to zod; i.e. `string.nullified` or `string.email.nullified`
export const helperSchema = {
    trimmedString,
    nonEmptyString,
    nullifiedInsuranceString: (max) => nullifyString(insuranceString(max)),
    insuranceString: (max) => insuranceString(max),
    /**
     * Converts falsy and white-space only strings into `null` when parsed, while still accepting `null` values.
     * Validates for `string` or `null` values removing whitespace, and transforms empty strings to `null`.
     * Inadvertently this ensures that all `string` results are non-empty.
     */
    nullifiedString: nullifyString(trimmedString),
    nullifiedEmail: nullifyString(z.email('Must be a valid email.')),
    csv: csvList,
    nullifiedUrl: nullifyString(z.url('Must be a valid URL.')),
    nullifiedEmailList: nullifyString(emailList), // ? probably needs a nicer message
    emailList,
    decimal,
    nonNegativeNumber: z
        .number({
        error: (iss) => iss.input === undefined
            ? 'Must provide a number.'
            : 'Must be a number.',
    })
        .nonnegative('Must be zero or a positive number.'),
    policyDays: z
        .number({
        error: (iss) => iss.input === undefined
            ? 'Must provide a number.'
            : 'Must be a number.',
    })
        .min(0, 'Must be zero or a positive number.')
        .max(maxPolicyEligibilityDays, 'Must be less than 1,000,000.'),
    optionalizedString: optionalizeString(trimmedString),
    optionalizedDatetime: optionalizeString(z.iso.datetime()),
};
//# sourceMappingURL=zodExtensions.js.map